import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { translate, useTranslate } from 'hooks/useTranslate';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import cleanText from 'utils/cleanText';
import { getNewOperationDate } from 'utils/getNewOperationDate';

import { cleanValue } from 'constants/formats';
import operationTypes from 'constants/operationTypes';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { useGetLastOperationDate } from 'modules/transactions/hooks/useGetLastOperationDate';
import { useGetHoldingClassOptionsForOperationModal } from 'modules/transactions/hooks/useGetHoldingClassOptionsForOperationModal';
import { executeCanje } from 'modules/transactions/redux/operationActions';
import {
  fetchCanjePreview,
  fetchPartnerCategoriesForSociety,
} from 'modules/transactions/api/canjeApi';
import { getUserSocieties } from 'modules/society/redux/societyActions';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';

import { CanjeModalContextType } from '../types/CanjeModalContextType';
import {
  CANJE_EXCLUDED_PARTNER_IDS,
  CANJE_EXCLUDED_PARTNER_IDS_KEY,
} from '../constants/canjeExcludedPartnerIds';
import { canjeModalInitValue } from '../constants/canjeModalInitValue';
import { mapPartnerCategoriesToSelectOptions } from '../utils/mapPartnerCategoriesToSelectOptions';

const CanjeModalContext =
  createContext<CanjeModalContextType>(canjeModalInitValue);

export const CanjeModalProvider: FC<{
  children: ReactNode;
  /** Lo lee App.js vía modal.props.size (Bootstrap Modal) */
  size?: string;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const [date, setDate] = useState('');
  const [scriptureView, setScriptureView] = useState(true);
  const [ratioOld, setRatioOld] = useState<number>(0);
  const [ratioNew] = useState<number>(1);
  const [comments, setComments] = useState('');
  const [file, setFile] = useState<File | undefined>();
  const [notaryName, setNotaryName] = useState('');
  const [notaryProtocol, setNotaryProtocol] = useState('');
  const [notaryCity, setNotaryCity] = useState('');
  const [isPartnerCreationOnly, setIsPartnerCreationOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [targetSocietyId, setTargetSocietyId] = useState('');
  const [targetSocietyOption, setTargetSocietyOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [targetSocietyOptions, setTargetSocietyOptions] = useState<
    CustomAsyncSelectOption[]
  >([]);
  const [isLoadingSocieties, setIsLoadingSocieties] = useState(false);
  const [previewParticipants, setPreviewParticipants] = useState<
    CanjeModalContextType['data']['previewParticipants']
  >([]);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<
    string[]
  >([]);
  const [previewErrorKey, setPreviewErrorKey] = useState<string | null>(null);
  const [originShareClassName, setOriginShareClassName] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [originCategoryOptions, setOriginCategoryOptions] = useState<
    CustomAsyncSelectOption[]
  >([]);
  const [targetCategoryOptions, setTargetCategoryOptions] = useState<
    CustomAsyncSelectOption[]
  >([]);
  const [isLoadingOriginCategories, setIsLoadingOriginCategories] =
    useState(false);
  const [isLoadingTargetCategories, setIsLoadingTargetCategories] =
    useState(false);
  const [originCategoryAllOption, setOriginCategoryAllOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [originCategoryPeaksOption, setOriginCategoryPeaksOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [targetCategoryAllOption, setTargetCategoryAllOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [targetCategoryPeaksOption, setTargetCategoryPeaksOption] =
    useState<CustomAsyncSelectOption | null>(null);

  const { lastOperationDate, formattedLastOperationDate } =
    useGetLastOperationDate(actualSociety?._id).data;

  const originSocietyId = useMemo(
    () => String(actualSociety?._id || ''),
    [actualSociety?._id]
  );

  const {
    data: originHoldingData,
    actions: originHoldingActions,
    isLoading: isLoadingOriginHoldingClasses,
  } = useGetHoldingClassOptionsForOperationModal({
    societyId: originSocietyId,
    includeShares: true,
    autoSelectFirst: false,
  });

  const { data: holdingData, actions: holdingActions } =
    useGetHoldingClassOptionsForOperationModal({
      societyId: targetSocietyId,
      includeShares: true,
    });

  const loadAdministratedSocieties = useCallback(async () => {
    if (!user?._id || !user?.societies?.administrated?.length) {
      setTargetSocietyOptions([]);
      return;
    }
    try {
      setIsLoadingSocieties(true);
      const response = (await getUserSocieties(
        user._id,
        'admin',
        null,
        'table'
      )) as { society: { _id: string; name: string } }[] | null;

      if (!Array.isArray(response)) {
        setTargetSocietyOptions([]);
        return;
      }

      const originId = String(actualSociety?._id || '');
      const opts: CustomAsyncSelectOption[] = response
        .map((row) => ({
          value: row.society._id,
          label: row.society.name,
        }))
        .filter((o) => o.value !== originId);

      setTargetSocietyOptions(opts);
    } catch {
      setTargetSocietyOptions([]);
    } finally {
      setIsLoadingSocieties(false);
    }
  }, [user, actualSociety?._id]);

  useEffect(() => {
    loadAdministratedSocieties();
  }, [loadAdministratedSocieties]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setOriginCategoryAllOption(null);
      setOriginCategoryPeaksOption(null);
      const sid = actualSociety?._id;
      if (!sid) {
        setOriginCategoryOptions([]);
        return;
      }
      setIsLoadingOriginCategories(true);
      try {
        const list = await fetchPartnerCategoriesForSociety(String(sid));
        if (cancelled) return;
        setOriginCategoryOptions(
          mapPartnerCategoriesToSelectOptions(list, translate)
        );
      } catch {
        if (!cancelled) setOriginCategoryOptions([]);
      } finally {
        if (!cancelled) setIsLoadingOriginCategories(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [actualSociety?._id]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setTargetCategoryAllOption(null);
      setTargetCategoryPeaksOption(null);
      if (!targetSocietyId) {
        setTargetCategoryOptions([]);
        return;
      }
      setIsLoadingTargetCategories(true);
      try {
        const list = await fetchPartnerCategoriesForSociety(targetSocietyId);
        if (cancelled) return;
        setTargetCategoryOptions(
          mapPartnerCategoriesToSelectOptions(list, translate)
        );
      } catch {
        if (!cancelled) setTargetCategoryOptions([]);
      } finally {
        if (!cancelled) setIsLoadingTargetCategories(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [targetSocietyId]);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeDate = (e: ChangeEvent<HTMLInputElement>) => {
    setDate(e.target.value);
  };

  const handleChangeRatioOld = (e: ChangeEvent<HTMLInputElement>) => {
    setRatioOld(cleanValue(e.target.value));
  };

  const handleChangeComments = (comment: string) => {
    setComments(comment);
  };

  const handleChangeFile = (f: File) => {
    setFile(f);
  };

  const handleTargetSocietyChange = (opt: CustomAsyncSelectOption | null) => {
    setTargetSocietyOption(opt);
    setTargetSocietyId(opt?.value || '');
    holdingActions.handleClassOptionChange(null);
  };

  const handleToggleIsPartnerCreationOnly = () => {
    setIsPartnerCreationOnly((prev) => !prev);
  };

  const loadPreview = useCallback(async () => {
    if (
      !actualSociety?._id ||
      !originHoldingData.selectedHoldingClass ||
      ratioOld < 1
    ) {
      return;
    }
    setIsLoadingPreview(true);
    setPreviewErrorKey(null);
    try {
      const preview = await fetchCanjePreview({
        societyId: actualSociety._id,
        originShareClass: originHoldingData.selectedHoldingClass,
        ratioOld,
        ratioNew,
        ...(CANJE_EXCLUDED_PARTNER_IDS.length
          ? { excludedPartnerIds: CANJE_EXCLUDED_PARTNER_IDS }
          : {}),
      });
      setPreviewParticipants(preview.participants || []);
      setOriginShareClassName(preview.originShareClassName || '');
      setPreviewErrorKey(preview.errorKey);
    } catch {
      setPreviewParticipants([]);
      setPreviewErrorKey('CanjePreviewError');
    } finally {
      setIsLoadingPreview(false);
    }
  }, [
    actualSociety?._id,
    originHoldingData.selectedHoldingClass,
    ratioOld,
    ratioNew,
    CANJE_EXCLUDED_PARTNER_IDS_KEY,
  ]);

  useEffect(() => {
    if (scriptureView || !actualSociety?._id) {
      return;
    }
    if (!originHoldingData.selectedHoldingClass || ratioOld < 1) {
      setPreviewParticipants([]);
      setPreviewErrorKey(null);
      setOriginShareClassName('');
      return;
    }
    loadPreview();
  }, [
    scriptureView,
    actualSociety?._id,
    originHoldingData.selectedHoldingClass,
    ratioOld,
    loadPreview,
  ]);

  useEffect(() => {
    setSelectedParticipantIds([]);
  }, [previewParticipants]);

  const handleToggleParticipant = useCallback((participantId: string) => {
    setSelectedParticipantIds((prev) => {
      if (prev.includes(participantId)) {
        return prev.filter((id) => id !== participantId);
      }
      return [...prev, participantId];
    });
  }, []);

  const handleSelectAllParticipants = useCallback(() => {
    setSelectedParticipantIds(
      previewParticipants.map((participant) => participant.originPartnerId)
    );
  }, [previewParticipants]);

  const handleDeselectAllParticipants = useCallback(() => {
    setSelectedParticipantIds([]);
  }, []);

  const handleSaveCanje = async () => {
    if (scriptureView) {
      const hasValidDate = date && date >= formattedLastOperationDate;
      if (!hasValidDate || ratioOld < 1) {
        errorSwal(t('CanjeValidationStep1'));
        return;
      }
      setScriptureView(false);
      return;
    }

    if (!originHoldingData.selectedHoldingClass) {
      errorSwal(t('CanjeValidationOriginClass'));
      return;
    }

    if (!targetSocietyId || !holdingData.selectedHoldingClass) {
      errorSwal(t('CanjeValidationStep2'));
      return;
    }

    if (previewErrorKey || previewParticipants.length === 0) {
      errorSwal(t('CanjeNoParticipants'));
      return;
    }

    if (selectedParticipantIds.length === 0) {
      errorSwal(t('CanjeNoSelectedParticipants'));
      return;
    }

    try {
      setIsLoading(true);

      let uploadedDocument = null;
      if (file) {
        const newDocument = {
          file,
          date: new Date(date),
          size: file.size,
          name: operationTypes.CONTRIBUTION_FILENAME,
          author: user['_id'],
          society: actualSociety['_id'],
          fileType: fileTypes[file.type as keyof typeof fileTypes],
          category: documentTypes.SCRIPTURES,
          description: t('DocumentLinkedToOperation'),
        };
        uploadedDocument = await store.dispatch(addDocument(newDocument));
      }

      const newOperationDate = getNewOperationDate(lastOperationDate, date);

      const body: Record<string, unknown> = {
        society: actualSociety['_id'],
        targetSociety: targetSocietyId,
        originShareClass: originHoldingData.selectedHoldingClass,
        targetShareClass: holdingData.selectedHoldingClass,
        ratioOld,
        ratioNew,
        isPartnerCreationOnly,
        date: newOperationDate,
        user: user['_id'],
        documents: uploadedDocument?.['_id'] ? [uploadedDocument['_id']] : [],
        comments: cleanText(comments),
        notarialRegistration: {
          notaryName,
          notaryCity,
          protocolNr: notaryProtocol,
        },
      };

      if (originCategoryAllOption?.value) {
        body.originCategoryAll = originCategoryAllOption.value;
      }
      if (originCategoryPeaksOption?.value) {
        body.originCategoryPeaks = originCategoryPeaksOption.value;
      }
      if (targetCategoryAllOption?.value) {
        body.targetCategoryAll = targetCategoryAllOption.value;
      }
      if (targetCategoryPeaksOption?.value) {
        body.targetCategoryPeaks = targetCategoryPeaksOption.value;
      }

      const selectedSet = new Set(selectedParticipantIds);
      const userExcludedIds = previewParticipants
        .filter((participant) => !selectedSet.has(participant.originPartnerId))
        .map((participant) => participant.originPartnerId);
      const excludedPartnerIds = [
        ...new Set([...CANJE_EXCLUDED_PARTNER_IDS, ...userExcludedIds]),
      ];
      if (excludedPartnerIds.length > 0) {
        body.excludedPartnerIds = excludedPartnerIds;
      }

      const result = (await store.dispatch(executeCanje(body) as any)) as {
        capitalDecrease?: { operation?: unknown };
      } | null;

      setIsLoading(false);

      if (!result) {
        return;
      }

      if (result.capitalDecrease?.operation) {
        successSwal(t('CanjeSuccess'));
        handleCloseModal();
      } else {
        errorSwal(t('CanjeError'));
      }
    } catch {
      setIsLoading(false);
      errorSwal(t('CanjeError'));
    }
  };

  return (
    <CanjeModalContext.Provider
      value={{
        data: {
          date,
          ratioOld,
          ratioNew,
          comments,
          file,
          notaryName,
          notaryProtocol,
          notaryCity,
          scriptureView,
          lastOperationDate: formattedLastOperationDate,
          isPartnerCreationOnly,
          isLoading,
          targetSocietyId,
          targetSocietyOption,
          targetSocietyOptions,
          isLoadingSocieties,
          selectedOriginHoldingClass: originHoldingData.selectedHoldingClass,
          selectedOriginHoldingClassOption:
            originHoldingData.selectedHoldingClassOption,
          originHoldingClassOptions: originHoldingData.holdingClassOptions,
          isLoadingOriginHoldingClasses,
          selectedHoldingClass: holdingData.selectedHoldingClass,
          selectedHoldingClassOption: holdingData.selectedHoldingClassOption,
          holdingClassOptions: holdingData.holdingClassOptions,
          previewParticipants,
          selectedParticipantIds,
          previewErrorKey,
          originShareClassName,
          isLoadingPreview,
          societyActualShares: actualSociety?.sharesCount?.actual || 0,
          originCategoryOptions,
          targetCategoryOptions,
          isLoadingOriginCategories,
          isLoadingTargetCategories,
          originCategoryAllOption,
          originCategoryPeaksOption,
          targetCategoryAllOption,
          targetCategoryPeaksOption,
        },
        actions: {
          handleCloseModal,
          handleChangeDate,
          handleChangeRatioOld,
          handleChangeComments,
          handleChangeFile,
          handleChangeNotaryName: setNotaryName,
          handleChangeNotaryProtocol: setNotaryProtocol,
          handleChangeNotaryCity: setNotaryCity,
          handleTargetSocietyChange,
          handleToggleIsPartnerCreationOnly,
          handleChangeOriginHoldingClass:
            originHoldingActions.handleClassOptionChange,
          handleFetchOriginHoldingClassByTerm:
            originHoldingActions.handleFetchHoldingClassByTerm,
          handleChangeHoldingClass: holdingActions.handleClassOptionChange,
          handleFetchHoldingClassByTerm:
            holdingActions.handleFetchHoldingClassByTerm,
          handleChangeOriginCategoryAll: setOriginCategoryAllOption,
          handleChangeOriginCategoryPeaks: setOriginCategoryPeaksOption,
          handleChangeTargetCategoryAll: setTargetCategoryAllOption,
          handleChangeTargetCategoryPeaks: setTargetCategoryPeaksOption,
          handleToggleParticipant,
          handleSelectAllParticipants,
          handleDeselectAllParticipants,
          handleSaveCanje,
        },
      }}
    >
      {children}
    </CanjeModalContext.Provider>
  );
};

export const useCanjeModal = () => useContext(CanjeModalContext);
